import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ParseAPI } from "../../API";

export const SaveTheDateForm = () => {
    const [formData, setFormData] = useState({
        eventName: '',
        department: '',
        dayDateOfEvent: '',
        startTime: '',
        endTime: '',
        locationRequested: '',
        type: 'SaveTheDate' // Special type for Save the Date requests
    });
    
    const [loading, setLoading] = useState(false);
    const [formSaved, setFormSaved] = useState(false);
    const user = JSON.parse(localStorage.getItem('user'));
    const navigate = useNavigate();

    // Handle form field changes
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Handle form submission 
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            
            // Save the Save the Date request using ParseAPI
            const formToSave = {
                ...formData,
                isSaveTheDate: true,  // Flag to identify this as a Save the Date
                isDraft: false,       // Not a draft
                isFullyApproved: false // Needs approval
            };
            
            await ParseAPI.saveForm(formToSave, user, false);
            
            setFormSaved(true);
            setTimeout(() => {
                navigate('/home/request');
            }, 1500);
        } catch (error) {
            console.error('Error saving Save the Date request:', error);
            alert('Error saving form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-lg shadow-xl flex items-center space-x-4">
                        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-gray-700 font-medium">Processing...</span>
                    </div>
                </div>
            )}
            
            {formSaved && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-lg shadow-xl flex items-center space-x-4">
                        <svg className="h-8 w-8 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 font-medium">Date saved successfully!</span>
                    </div>
                </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Header */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-6 py-5 border-b border-gray-200 bg-indigo-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-2xl font-bold text-indigo-800">Save the Date Request</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Reserve a date for your upcoming event. You can convert this to a full event request later.
                                </p>
                            </div>
                            <img src="/bethel-favicon.png" alt="Bethel Logo" className="h-12 w-auto" />
                        </div>
                    </div>
                    
                    {/* Form Sections */}
                    <div className="bg-white px-6 py-6">
                        <h4 className="text-xl font-medium text-gray-900 mb-4">Event Information</h4>
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="sm:col-span-6">
                                <label htmlFor="eventName" className="block text-sm font-medium text-gray-700">Event Name</label>
                                <div className="mt-1">
                                    <input 
                                        required 
                                        placeholder="Worship Night" 
                                        type="text" 
                                        name="eventName" 
                                        id="eventName" 
                                        value={formData.eventName} 
                                        onChange={handleChange}
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                            
                            <div className="sm:col-span-3">
                                <label htmlFor="department" className="block text-sm font-medium text-gray-700">Department</label>
                                <div className="mt-1">
                                    <input
                                        required
                                        type="text"
                                        name="department"
                                        id="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        placeholder="Enter department name"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                            
                            <div className="sm:col-span-3">
                                <div className="flex justify-between items-center">
                                    <label htmlFor="locationRequested" className="block text-sm font-medium text-gray-700">Location Requested</label>
                                    <a 
                                        href="/campus-map.pdf" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                        </svg>
                                        Campus Map
                                    </a>
                                </div>
                                <div className="mt-1">
                                    <input 
                                        required 
                                        type="text" 
                                        name="locationRequested" 
                                        id="locationRequested" 
                                        value={formData.locationRequested} 
                                        onChange={handleChange} 
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        placeholder="Enter location"
                                    />
                                </div>
                            </div>
                            
                            <div className="sm:col-span-2">
                                <label htmlFor="dayDateOfEvent" className="block text-sm font-medium text-gray-700">Event Date</label>
                                <div className="mt-1">
                                    <input 
                                        required 
                                        type="date" 
                                        name="dayDateOfEvent" 
                                        id="dayDateOfEvent" 
                                        value={formData.dayDateOfEvent} 
                                        onChange={handleChange} 
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                    />
                                </div>
                            </div>
                            
                            <div className="sm:col-span-2">
                                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">Start Time</label>
                                <div className="mt-1">
                                    <input 
                                        required 
                                        type="time" 
                                        name="startTime" 
                                        id="startTime" 
                                        value={formData.startTime} 
                                        onChange={handleChange} 
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                    />
                                </div>
                            </div>
                            
                            <div className="sm:col-span-2">
                                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">End Time</label>
                                <div className="mt-1">
                                    <input 
                                        required 
                                        type="time" 
                                        name="endTime" 
                                        id="endTime" 
                                        value={formData.endTime} 
                                        onChange={handleChange} 
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Form Actions */}
                <div className="px-6 py-4 bg-gray-50 rounded-lg shadow flex justify-end space-x-4">
                    <button 
                        type="button" 
                        onClick={() => navigate('/home/request')}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Save Date
                    </button>
                </div>
            </form>
        </div>
    );
};